import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  cn_template: '',
  tr_template: '',
  apply_to_existing_users: true,
};

const EMPTY_PREVIEW = {
  cn_address: '',
  tr_address: '',
};

export default function AdminAddresses() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(EMPTY_PREVIEW);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    try {
      const response = await axios.get('/api/admin/addresses');
      setForm({
        cn_template: response.data.cn_template || '',
        tr_template: response.data.tr_template || '',
        apply_to_existing_users: true,
      });
      setPreview(response.data.preview || EMPTY_PREVIEW);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load address templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const livePreview = useMemo(() => {
    const render = (template) => String(template || '')
      .replace(/\{\{\s*shipping_mark\s*\}\}/gi, 'A1-D2D-Semebia')
      .replace(/\{\{\s*customer_name\s*\}\}/gi, 'Semebia')
      .replace(/\{\{\s*full_name\s*\}\}/gi, 'Semebia')
      .trim();

    return {
      cn_address: render(form.cn_template) || preview.cn_address,
      tr_address: render(form.tr_template) || preview.tr_address,
    };
  }, [form.cn_template, form.tr_template, preview.cn_address, preview.tr_address]);

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const response = await axios.put('/api/admin/addresses', form);
      setPreview(response.data.preview || EMPTY_PREVIEW);
      toast.success(response.data.message || 'Address templates saved');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save address templates');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-center"><div className="spinner spinner-dark" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">←</Link>
        <h1>Warehouse Addresses</h1>
      </div>

      <div className="page-content">
        <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <p style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>Template placeholders</p>
          <p style={{ fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
            Use <strong>{'{{shipping_mark}}'}</strong> anywhere you want the customer shipping mark to appear.
            You can also use <strong>{'{{customer_name}}'}</strong> or <strong>{'{{full_name}}'}</strong> if you ever want the customer name inside the address.
          </p>
          <p style={{ fontSize: 12, color: '#1e40af', marginTop: 10 }}>
            Saving with "refresh all users" turned on will rewrite every customer&apos;s stored CN and TR address using these templates.
          </p>
        </div>

        <div className="card">
          <div className="input-group">
            <label>China Address Template</label>
            <textarea
              className="input-field"
              rows={7}
              value={form.cn_template}
              onChange={(event) => setForm((previous) => ({ ...previous, cn_template: event.target.value }))}
              placeholder="Enter the China warehouse template"
              style={{ minHeight: 180, resize: 'vertical' }}
            />
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}>
            <label>Turkey Address Template</label>
            <textarea
              className="input-field"
              rows={7}
              value={form.tr_template}
              onChange={(event) => setForm((previous) => ({ ...previous, tr_template: event.target.value }))}
              placeholder="Enter the Turkey warehouse template"
              style={{ minHeight: 180, resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Preview with sample mark</p>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Sample shipping mark: <strong>A1-D2D-Semebia</strong></p>

          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12 }}>
                <p style={{ fontWeight: 700, marginBottom: 8 }}>China Preview</p>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                {livePreview.cn_address}
                </pre>
              </div>

            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12 }}>
              <p style={{ fontWeight: 700, marginBottom: 8 }}>Turkey Preview</p>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                {livePreview.tr_address}
              </pre>
            </div>
          </div>
        </div>

        <div className="card">
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={form.apply_to_existing_users}
              onChange={(event) => setForm((previous) => ({ ...previous, apply_to_existing_users: event.target.checked }))}
              style={{ marginTop: 3 }}
            />
            <span>Refresh all existing customer addresses when saving</span>
          </label>

          <button className="btn btn-primary btn-full" onClick={saveTemplates} disabled={saving} style={{ marginTop: 14 }}>
            {saving ? <span className="spinner" /> : 'Save Address Templates'}
          </button>
        </div>
      </div>
    </div>
  );
}
